// Layer 4 (UI) — the Cost & revenue route, per the locked comp
// (`.impeccable/mocks/decision/cost.html`, variant A · Margin strip, picked
// 25 Sep): the header's period switch, the lead band (margin strip, the
// categories ranked by overrun, daily margin), then six ink tabs, each with
// its headline figure. The period drives the band on every tab and the bodies
// of tabs 1–3; the matrix, register and pipeline read the whole register.

import { useEffect, useState, type ReactNode } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import {
  HelpAnchor,
  SurfacePageBody,
  SurfacePageHeader,
  SurfacePageLayout,
  SurfacePageTabs,
  Table,
  TBody,
  TD,
  TH,
  THead,
  TR,
} from '@/design-system';
import { Commentable } from '@/shared/comments/Commentable';
import { cn } from '@/shared/cn';
import { CARD_TABLE, DowntimeCard, HOT_ROW, SYN_CHIP } from '@/versions/v1/modules/downtime/DowntimeCard';
import { projectTileBySlug } from '@/versions/v1/shared/project-seed';
import { CostBand, CostPeriodSegment } from './CostBand';
import { costTabHeadlines } from './CostBand.transform';
import { CostPLTable } from './CostPLTable';
import { CostPerTonne } from './CostPerTonne';
import { ImprovementMatrix } from './ImprovementMatrix';
import { ImprovementPipeline } from './ImprovementPipeline';
import { PIPELINE_STAGES, improvementStageLabel } from './ImprovementPipeline.transform';
import { NsrTab } from './NsrTab';
import {
  COST_TABS,
  type CostCategoryName,
  type CostPeriod,
  type CostTab,
  type ImprovementRow,
  costCopy,
  costPeriod,
  improvementRegister,
  isCostPeriod,
  isCostTab,
} from './Scene.transform';
import type { ImprovementStage } from './seed';

/** Six tabs, each with its headline figure, fill one row at 1470. Below that
 *  the row tightens (padding, gaps, a 12 px label) so it stays one row at
 *  1280 rather than wrapping its last tab. K18: the pad is `px-1` since the
 *  US$ glyph widened three tab figures by one character each. */
const TAB_ROW =
  'pt-3 max-[1439px]:gap-0 max-[1439px]:[&>button]:gap-1 max-[1439px]:[&>button]:px-1 max-[1439px]:[&>button]:text-[12px]';

/** The build-up opens on the category the band ranks first. */
const DEFAULT_OPEN: readonly CostCategoryName[] = ['Maintenance'];
const DEFAULT_STAGE: ImprovementStage = 'priced';

function isStage(value: string | null): value is ImprovementStage {
  return PIPELINE_STAGES.some((stage) => stage === value);
}

export function CostScene() {
  const { projectId } = useParams<{ projectId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const project = projectTileBySlug(projectId);
  const copy = costCopy(project?.name ?? 'this plant');

  const tabParam = searchParams.get('tab');
  const periodParam = searchParams.get('period');
  const stageParam = searchParams.get('stage');
  const tab: CostTab = isCostTab(tabParam) ? tabParam : 'pl';
  // The period lives in the URL, so it survives a tab change, a reload and a
  // shared link, and it drives the band on every tab.
  const period: CostPeriod = isCostPeriod(periodParam) ? periodParam : 'month';
  const stage: ImprovementStage = isStage(stageParam) ? stageParam : DEFAULT_STAGE;
  const [openCategories, setOpenCategories] = useState<readonly CostCategoryName[]>(DEFAULT_OPEN);

  const headlines = costTabHeadlines(period);
  const tabItems = COST_TABS.map((item) => ({ ...item, count: headlines[item.id] }));

  useEffect(() => {
    document.title = project ? `${copy.title} · ${project.name}` : `${copy.title} · OptiFlow`;
  }, [copy.title, project]);

  const update = (entries: Record<string, string | null>) => {
    const next = new URLSearchParams(searchParams);
    for (const [key, value] of Object.entries(entries)) {
      if (value === null) next.delete(key);
      else next.set(key, value);
    }
    setSearchParams(next, { replace: true });
  };

  const selectTab = (value: string) => {
    if (isCostTab(value)) update({ tab: value });
  };
  const selectPeriod = (value: CostPeriod) => update({ period: value });
  const openOpportunity = (id: string) => update({ tab: 'improvements', opportunity: id });
  const openBuildUp = (category: CostCategoryName) => {
    setOpenCategories([category]);
    update({ tab: 'cost-per-tonne' });
  };
  const toggleCategory = (category: CostCategoryName) =>
    setOpenCategories((open) => (open.includes(category) ? open.filter((c) => c !== category) : [...open, category]));

  if (!project) {
    return (
      <SurfacePageLayout width="full">
        <SurfacePageBody>
          <SurfacePageHeader title="Plant not found" />
        </SurfacePageBody>
      </SurfacePageLayout>
    );
  }

  return (
    <SurfacePageLayout width="full">
      <Commentable name="CostScene">
        <SurfacePageBody className="min-h-[calc(100vh-3.2rem)] space-y-3" width="full">
          <SurfacePageHeader
            title={copy.title}
            titleSize="large"
            actions={
              <>
                <span className="text-xs tabular-nums text-muted" data-cost-range>
                  {costPeriod(period).dateRange}
                </span>
                <CostPeriodSegment period={period} onChange={selectPeriod} />
                {SYN_CHIP}
                <HelpAnchor doc="cost/overview" label="Cost & revenue" />
              </>
            }
          />
          <CostBand period={period} onOpenBuildUp={openBuildUp} />
          <SurfacePageTabs
            aria-label="Cost and revenue"
            items={tabItems}
            value={tab}
            onChange={selectTab}
            variant="ink"
            className={TAB_ROW}
          />
          {tab === 'pl' ? <CostPLTable period={period} /> : null}
          {tab === 'cost-per-tonne' ? (
            <CostPerTonne period={period} openCategories={openCategories} onToggleCategory={toggleCategory} />
          ) : null}
          {tab === 'nsr' ? <NsrTab period={period} onOpenOpportunity={openOpportunity} /> : null}
          {tab === 'matrix' ? <ImprovementMatrix onOpenOpportunity={openOpportunity} /> : null}
          {tab === 'improvements' ? (
            <ImprovementsTab
              selectedId={searchParams.get('opportunity')}
              onSelect={(id) => update({ opportunity: id })}
            />
          ) : null}
          {tab === 'pipeline' ? (
            <ImprovementPipeline
              stage={stage}
              onSelectStage={(value) => update({ stage: value })}
              onOpenOpportunity={openOpportunity}
            />
          ) : null}
        </SurfacePageBody>
      </Commentable>
    </SurfacePageLayout>
  );
}

function ImprovementsTab({ selectedId, onSelect }: { selectedId: string | null; onSelect: (id: string) => void }) {
  const { rows } = improvementRegister();
  const selected = rows.find((row) => row.id === selectedId) ?? rows[0];

  return (
    <Commentable name="ImprovementRegister">
      <div className="grid min-w-0 gap-3 xl:grid-cols-12 xl:items-start">
        <DowntimeCard title="Register" meta="ranked by payback" actions={SYN_CHIP} className="xl:col-span-8">
          <div className={CARD_TABLE}>
            <Table wrap={false} clickableRows className="tabular-nums" aria-label="Opportunity register">
              <THead>
                <TR>
                  <TH>ID</TH>
                  <TH>Opportunity</TH>
                  <TH>Stage</TH>
                  {/* K18: US$ widened the money cells; the Benefit column drops its
                      left pad so the register stays inside its panel at 1280.
                      K18 r1: the Capex column keeps its left pad — dropping both
                      collapsed the Benefit→Capex gap to 7 px, so the pair read as
                      one run. One pad is enough to fit. */}
                  <TH className="!pl-0 text-right">Benefit / yr</TH>
                  <TH className="!pl-0 text-right">Capex</TH>
                  <TH className="text-right">Payback</TH>
                </TR>
              </THead>
              <TBody>
                {rows.map((row) => {
                  const hot = row.id === selected?.id;
                  return (
                    <TR
                      key={row.id}
                      onRowClick={() => onSelect(row.id)}
                      className={hot ? HOT_ROW : undefined}
                      aria-selected={hot}
                      data-register-row={row.id}
                    >
                      <TD className="whitespace-nowrap font-mono text-xs text-ink-primary">{row.id}</TD>
                      <TD className="min-w-[12rem]">
                        <span className="line-clamp-2 font-semibold text-ink-primary">{row.opportunity}</span>
                        <span className="block text-xs capitalize text-muted">{row.category}</span>
                      </TD>
                      <TD className="whitespace-nowrap">{improvementStageLabel(row.stage)}</TD>
                      <TD className="whitespace-nowrap !pl-0 text-right text-ink-primary">{row.benefitPerYear}</TD>
                      <TD className="whitespace-nowrap text-right">{row.capex}</TD>
                      <TD className="whitespace-nowrap text-right font-semibold text-ink-primary">{row.paybackYears}</TD>
                    </TR>
                  );
                })}
              </TBody>
            </Table>
          </div>
        </DowntimeCard>
        {selected ? <ImprovementDetail row={selected} /> : null}
      </div>
    </Commentable>
  );
}

/** A caps label over hairline-divided fact rows: the detail's one grammar,
 *  with no box nested in the card. */
function Facts({ label, children }: { label: string; children: ReactNode }) {
  return (
    <section className="mt-3">
      <p className="text-[11px] font-semibold uppercase tracking-[0.07em] text-muted">{label}</p>
      <div className="mt-1 divide-y divide-[var(--hairline)]">{children}</div>
    </section>
  );
}

function FactRow({ label, value, kv = false }: { label: ReactNode; value: ReactNode; kv?: boolean }) {
  return (
    <div className={cn('grid gap-3 py-1.5 text-[13px]', kv ? 'grid-cols-[34%_1fr]' : 'grid-cols-[1fr_auto] items-baseline')}>
      <span className="text-ink-secondary">{label}</span>
      <span className={cn('tabular-nums text-ink-primary', kv ? 'font-semibold' : 'text-right font-semibold')}>{value}</span>
    </div>
  );
}

function StagePill({ children }: { children: ReactNode }) {
  return (
    <span className="inline-flex items-center rounded-full border border-[var(--hairline-strong)] bg-[var(--surface-1)] px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.06em] text-ink-secondary">
      {children}
    </span>
  );
}

function ImprovementDetail({ row }: { row: ImprovementRow }) {
  return (
    <DowntimeCard className="xl:col-span-4" aria-label={`${row.id} detail`}>
      {/* The title wraps beside its id rather than pushing the id to a line of
          its own in the narrow column. */}
      <header className="mb-2.5 flex items-start gap-3">
        <h3 className="min-w-0 flex-1 text-sm font-semibold text-ink-primary">{row.opportunity}</h3>
        <span className="shrink-0 pt-px font-mono text-xs text-muted">{row.id}</span>
      </header>
      <div className="flex items-center gap-2" data-register-detail={row.id}>
        <StagePill>{improvementStageLabel(row.stage)}</StagePill>
        <span className="text-xs capitalize text-muted">{row.category}</span>
      </div>
      <p className="mt-2 text-[13px] text-ink-secondary">{row.evidence}</p>
      {row.capacityCase ? (
        <Facts label="Constraint-removal case">
          {row.capacityCase.rows.map((item) => (
            <FactRow key={item.label} label={item.label} value={item.value} kv />
          ))}
        </Facts>
      ) : (
        <>
          <Facts label="Benefit terms">
            {row.benefitTerms.length > 0 ? (
              row.benefitTerms.map((term) => <FactRow key={term.label} label={term.label} value={term.amount} />)
            ) : (
              <FactRow label={<span className="text-muted">—</span>} value="" />
            )}
          </Facts>
          <div className="mt-3 grid grid-cols-2 gap-3 border-t border-[var(--hairline)] pt-2">
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-[0.07em] text-muted">Capex</p>
              <p className="text-base font-semibold tabular-nums text-ink-primary">{row.capex}</p>
            </div>
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-[0.07em] text-muted">Payback</p>
              <p className="text-base font-semibold tabular-nums text-ink-primary">{row.paybackYears}</p>
            </div>
          </div>
        </>
      )}
      <Facts label="Exploit → Subordinate → Elevate">
        <FactRow
          label={row.exploitSubordinateElevate.note}
          value={<StagePill>{row.exploitSubordinateElevate.tried ? 'Tried within capacity' : 'Not yet tried'}</StagePill>}
        />
      </Facts>
    </DowntimeCard>
  );
}
